// The offline queue of HACCP records (docs/architecture.md §22.8): a cooling started, a reception or a cleaning ticked
// on a phone without network is kept on the phone with its client ID and its original filled_at, and sent when the
// network is back, in the order it was entered. The record contract (§22.1) makes sending again safe: the same ID with
// the same content returns the record already saved.
//
// This file is the logic, independent of the browser and of React (tested in test/haccpQueue.test.ts): the queue over
// a storage (IndexedDB, else localStorage without the photos, else memory), and how each answer is handled. The
// dashboard's wiring (senders, triggers, the hook) is lib/haccpSync.ts.

import { ApiError, type UploadPurpose } from './api.ts';

export type QueueKind = 'cooling' | 'reception' | 'cleaning';

/** What the lists show while a record waits, and what its author re-enters from if it is refused. */
export type QueueSummary = Record<string, string | number | boolean | null>;

export interface QueuedRecord {
  /** The record's client ID (body.id): the queue's key. */
  id: string;
  /** Whose record, in which organisation: only that user, signed in, ever sends it or sees it. */
  userId: string;
  orgId: string;
  kind: QueueKind;
  /** The request, exactly as it will be sent (filled_at is when it was entered); the photo's upload ID is added once uploaded. */
  body: { id: string; filled_at: string } & Record<string, unknown>;
  /** A photo still to upload before the record, and where its upload ID goes in the body. */
  photo?: { blob: Blob; purpose: UploadPurpose; field: string } | undefined;
  /** The photo could not be kept (localStorage has no room for files) and the page was reloaded. */
  photoLost?: boolean | undefined;
  summary: QueueSummary;
  /** Order of entry. */
  seq: number;
  queuedAt: string;
  attempts: number;
  /** Not before this time (ms), after a failed attempt. */
  nextAt: number;
  state: 'pending' | 'failed';
  /** Why it was refused (state failed). */
  error?: { status: number; code: string; message: string } | undefined;
  /** Kept in memory only: the phone's storage refused it (full), it was being sent online. */
  volatile?: boolean | undefined;
}

export interface QueueStore {
  all(): Promise<QueuedRecord[]>;
  put(r: QueuedRecord): Promise<void>;
  delete(id: string): Promise<void>;
}

export interface QueueSender {
  upload(orgId: string, blob: Blob, purpose: UploadPurpose): Promise<string>;
  create(r: QueuedRecord): Promise<unknown>;
}

/** Photos kept on the phone at most (bytes, all queued records together): phone storage is not unlimited. */
export const QUEUE_PHOTO_BUDGET = 60 * 1024 * 1024;

/** The phone's storage cannot keep this record (full, or the photos' budget is spent). */
export class QueueFullError extends Error {
  constructor() {
    super('queue_full');
  }
}

/**
 * How an attempt ended: done; retry later (network, server error, rate limit); failed, the server refused the record
 * and sending it again will not change that (a 4xx: invalid, record_conflict, occurrence_done, task_archived…);
 * auth, the session has ended: wait for the same user to sign in again.
 */
export type Outcome = 'done' | 'retry' | 'failed' | 'auth';

export function classify(err: unknown): Exclude<Outcome, 'done'> {
  if (!(err instanceof ApiError)) return 'retry';
  if (err.status === 401) return 'auth';
  if (err.status === 408 || err.status === 429 || err.status >= 500) return 'retry';
  return 'failed';
}

/** The wait after the n-th failed attempt: 5 s, 10 s, 20 s… up to 5 minutes. */
export function backoff(attempts: number): number {
  return Math.min(5 * 60_000, 5_000 * 2 ** Math.max(0, attempts - 1));
}

export interface Scope {
  userId: string;
  /** The organisations the user may send to (their memberships). */
  orgIds: readonly string[];
}

export interface NewRecord {
  userId: string;
  orgId: string;
  kind: QueueKind;
  body: QueuedRecord['body'];
  photo?: QueuedRecord['photo'];
  summary: QueueSummary;
}

export type SubmitResult<T> = { status: 'sent'; result: T } | { status: 'queued' };

export interface QueueOptions {
  now?: () => number;
  online?: () => boolean;
  /** Runs the drain under a lock shared by the tabs (Web Locks), so two tabs do not send the same record at once. */
  lock?: <T>(fn: () => Promise<T>) => Promise<T>;
  /** Called after a drain with the organisations where records were saved (to refresh their lists). */
  onSaved?: (orgIds: string[]) => void;
}

export class HaccpQueue {
  private items: QueuedRecord[] = [];
  private loaded: Promise<void> | null = null;
  private listeners = new Set<() => void>();
  private chain: Promise<unknown> = Promise.resolve();
  private results = new Map<string, unknown>();
  private errors = new Map<string, unknown>();
  private readonly now: () => number;
  private readonly online: () => boolean;
  private readonly lock: <T>(fn: () => Promise<T>) => Promise<T>;
  /** The state the indicator shows. */
  state = { syncing: false, unreachable: false, authBlocked: false };

  private readonly store: QueueStore;
  private readonly sender: QueueSender;
  private readonly opts: QueueOptions;

  constructor(store: QueueStore, sender: QueueSender, opts: QueueOptions = {}) {
    this.store = store;
    this.sender = sender;
    this.opts = opts;
    this.now = opts.now ?? Date.now;
    this.online = opts.online ?? (() => true);
    this.lock = opts.lock ?? ((fn) => fn());
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** The queued records, in order (a new array after each change, for useSyncExternalStore). */
  snapshot(): readonly QueuedRecord[] {
    return this.items;
  }

  private emit(): void {
    this.items = [...this.items].sort((a, b) => a.seq - b.seq);
    this.state = { ...this.state };
    for (const fn of this.listeners) fn();
  }

  /** Reads the storage again (another tab may have sent or added records); memory-only records stay. */
  async reload(): Promise<void> {
    let stored: QueuedRecord[];
    try {
      stored = await this.store.all();
    } catch {
      return;
    }
    const ids = new Set(stored.map((r) => r.id));
    this.items = [...stored, ...this.items.filter((r) => r.volatile && !ids.has(r.id))];
    this.emit();
  }

  load(): Promise<void> {
    this.loaded ??= this.reload();
    return this.loaded;
  }

  private exclusive<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.chain.then(() => this.lock(fn));
    this.chain = run.catch(() => undefined);
    return run;
  }

  private async save(r: QueuedRecord): Promise<void> {
    this.items = [...this.items.filter((x) => x.id !== r.id), r];
    if (!r.volatile) {
      try {
        await this.store.put(r);
      } catch {
        // Storage refused an update (full): the record stays in memory, as it was stored before.
      }
    }
    this.emit();
  }

  private async remove(id: string): Promise<void> {
    this.items = this.items.filter((x) => x.id !== id);
    try {
      await this.store.delete(id);
    } catch {
      // gone with the storage
    }
    this.emit();
  }

  /** Queues a record. QueueFullError if the phone cannot keep it, unless `allowVolatile` (it is being sent now). */
  async add(n: NewRecord, allowVolatile = false): Promise<QueuedRecord> {
    await this.load();
    const last = this.items.reduce((m, r) => Math.max(m, r.seq), 0);
    const r: QueuedRecord = { id: n.body.id, userId: n.userId, orgId: n.orgId, kind: n.kind, body: n.body, photo: n.photo,
      summary: n.summary, seq: Math.max(this.now(), last + 1), queuedAt: new Date(this.now()).toISOString(), attempts: 0,
      nextAt: 0, state: 'pending' };
    const photoBytes = this.items.reduce((s, x) => s + (x.photo?.blob.size ?? 0), 0) + (n.photo?.blob.size ?? 0);
    let stored = photoBytes <= QUEUE_PHOTO_BUDGET;
    if (stored) {
      try {
        await this.store.put(r);
      } catch {
        stored = false;
      }
    }
    if (!stored) {
      if (!allowVolatile) throw new QueueFullError();
      r.volatile = true;
    }
    this.items = [...this.items.filter((x) => x.id !== r.id), r];
    this.emit();
    return r;
  }

  /**
   * Sends the scope's pending records, in order. A record in its backoff stops the drain unless `force` (the network
   * came back, the user asked); a retryable failure stops it too (the next ones would fail alike, and order is kept);
   * a refused record is set aside (failed) and the drain goes on.
   */
  drain(scope: Scope, force = false): Promise<void> {
    if (!this.online()) return Promise.resolve();
    return this.exclusive(async () => {
      await this.reload();
      const saved = new Set<string>();
      this.state.syncing = true;
      this.emit();
      try {
        const mine = this.items.filter((r) => r.userId === scope.userId && scope.orgIds.includes(r.orgId) && r.state === 'pending');
        for (const r of mine) {
          if (!force && r.nextAt > this.now()) break;
          const outcome = await this.attempt(r);
          if (outcome === 'done') saved.add(r.orgId);
          if (outcome === 'retry' || outcome === 'auth') break;
        }
      } finally {
        this.state.syncing = false;
        this.emit();
        if (saved.size > 0) this.opts.onSaved?.([...saved]);
      }
    });
  }

  private async attempt(r0: QueuedRecord): Promise<Outcome> {
    let r = r0;
    try {
      if (r.photo && r.body[r.photo.field] === undefined) {
        const uploadId = await this.sender.upload(r.orgId, r.photo.blob, r.photo.purpose);
        // From now on the request is fixed, upload ID included: a retry must send exactly the same body.
        r = { ...r, body: { ...r.body, [r.photo.field]: uploadId }, photo: undefined };
        await this.save(r);
      } else if (r.photoLost) {
        await this.save({ ...r, state: 'failed', error: { status: 0, code: 'photo_lost', message: 'photo_lost' } });
        return 'failed';
      }
      const result = await this.sender.create(r);
      this.results.set(r.id, result);
      this.state.unreachable = false;
      this.state.authBlocked = false;
      await this.remove(r.id);
      return 'done';
    } catch (err) {
      const outcome = classify(err);
      if (outcome === 'failed') {
        const e = err as ApiError;
        this.errors.set(r.id, err);
        this.state.unreachable = false;
        await this.save({ ...r, state: 'failed', error: { status: e.status, code: e.code, message: e.message } });
      } else if (outcome === 'auth') {
        this.state.authBlocked = true;
        this.emit();
      } else {
        this.state.unreachable = !(err instanceof ApiError);
        await this.save({ ...r, attempts: r.attempts + 1, nextAt: this.now() + backoff(r.attempts + 1) });
      }
      return outcome;
    }
  }

  /**
   * Saves a record from a form: queued first, then sent at once (after any older record of the same user) when online.
   * 'sent' with the server's answer, or 'queued' (no network, server unavailable, session ended). A refusal is thrown
   * (the ApiError, with its field errors) and the record leaves the queue: the form is still open to change it.
   */
  async submit<T>(n: NewRecord, scope: Scope): Promise<SubmitResult<T>> {
    const online = this.online();
    await this.add(n, online);
    if (!online) return { status: 'queued' };
    await this.drain(scope, true);
    const id = n.body.id;
    if (this.results.has(id)) {
      const result = this.results.get(id) as T;
      this.results.delete(id);
      return { status: 'sent', result };
    }
    const r = this.items.find((x) => x.id === id);
    if (r?.state === 'failed') {
      const err = this.errors.get(id);
      this.errors.delete(id);
      await this.remove(id);
      throw err ?? new ApiError(r.error?.status ?? 400, null);
    }
    return { status: 'queued' };
  }

  /** Sends a record again now (pending: skip its backoff; failed: the user thinks it may pass now). */
  async retry(id: string, scope: Scope): Promise<void> {
    const r = this.items.find((x) => x.id === id);
    if (!r) return;
    await this.save({ ...r, state: 'pending', error: undefined, attempts: 0, nextAt: 0 });
    await this.drain(scope, true);
  }

  /** Removes a record from the phone (a refused one, re-entered or given up by its author). */
  discard(id: string): Promise<void> {
    this.errors.delete(id);
    return this.remove(id);
  }
}

// ---------------------------------------------------------------- storages

/** In memory: for tests, and when the browser offers no storage at all (private modes). */
export class MemoryStore implements QueueStore {
  private m = new Map<string, QueuedRecord>();
  async all() {
    return [...this.m.values()].map((r) => ({ ...r }));
  }
  async put(r: QueuedRecord) {
    this.m.set(r.id, { ...r });
  }
  async delete(id: string) {
    this.m.delete(id);
  }
}

const DB = 'cck-haccp-queue';
const OBJECTS = 'records';

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** IndexedDB, photos included (Blobs are stored as they are). */
export class IdbStore implements QueueStore {
  private readonly db: IDBDatabase;
  private constructor(db: IDBDatabase) {
    this.db = db;
  }

  static async open(): Promise<IdbStore> {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(OBJECTS, { keyPath: 'id' });
    return new IdbStore(await request(req));
  }

  private objects(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(OBJECTS, mode).objectStore(OBJECTS);
  }
  all() {
    return request(this.objects('readonly').getAll() as IDBRequest<QueuedRecord[]>);
  }
  async put(r: QueuedRecord) {
    await request(this.objects('readwrite').put(r));
  }
  async delete(id: string) {
    await request(this.objects('readwrite').delete(id));
  }
}

const LOCAL_KEY = 'cck.haccpQueue';

/**
 * localStorage, when IndexedDB is unavailable: records as JSON, photos only in memory (localStorage holds text, a
 * few megabytes). A record whose photo was lost with a reload is set aside for its author to enter again.
 */
export class LocalStore implements QueueStore {
  private photos = new Map<string, QueuedRecord['photo']>();
  private readonly storage: Storage;
  constructor(storage: Storage) {
    this.storage = storage;
  }

  private read(): QueuedRecord[] {
    try {
      return JSON.parse(this.storage.getItem(LOCAL_KEY) ?? '[]') as QueuedRecord[];
    } catch {
      return [];
    }
  }
  private write(list: QueuedRecord[]): void {
    this.storage.setItem(LOCAL_KEY, JSON.stringify(list));
  }
  async all() {
    return this.read().map((r) => {
      const photo = this.photos.get(r.id);
      return photo ? { ...r, photo, photoLost: undefined } : r;
    });
  }
  async put(r: QueuedRecord) {
    const { photo, ...rest } = r;
    if (photo) this.photos.set(r.id, photo);
    else this.photos.delete(r.id);
    this.write([...this.read().filter((x) => x.id !== r.id), { ...rest, ...(photo ? { photoLost: true } : {}) }]);
  }
  async delete(id: string) {
    this.photos.delete(id);
    this.write(this.read().filter((x) => x.id !== id));
  }
}

/** The best storage the browser offers. */
export async function openStore(): Promise<QueueStore> {
  try {
    if (typeof indexedDB !== 'undefined') return await IdbStore.open();
  } catch {
    // IndexedDB refused (some private modes)
  }
  try {
    localStorage.setItem(`${LOCAL_KEY}.probe`, '1');
    localStorage.removeItem(`${LOCAL_KEY}.probe`);
    return new LocalStore(localStorage);
  } catch {
    return new MemoryStore();
  }
}

/** A store that opens on first use (the queue is created before the storage is ready). */
export function lazyStore(open: () => Promise<QueueStore>): QueueStore {
  let s: Promise<QueueStore> | null = null;
  const get = () => (s ??= open());
  return {
    all: async () => (await get()).all(),
    put: async (r) => (await get()).put(r),
    delete: async (id) => (await get()).delete(id),
  };
}
